import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import {
  resolveBoundDocumentIdentityFromPage,
  resolveDocumentIdentityFromPageSnapshot,
} from '@/common/utils/documentTransferIdentity'

export type FormEventParamIdentity = {
  _id: number
  _typeId: string
}

function readEntrySessionId(entry: unknown): string | null {
  const direct = readPlatformSessionIdFromPageEntry(entry)
  if (direct) return direct
  if (!entry || typeof entry !== 'object') return null
  const nested = (entry as { data?: unknown }).data
  return readPlatformSessionIdFromPageEntry(nested)
}

function readEntryDocumentIdentity(entry: unknown): { _id: number; _typeId: string } | null {
  const fromBound = resolveBoundDocumentIdentityFromPage(entry)
  if (fromBound) return { _id: fromBound._id, _typeId: fromBound._typeId }
  const fromSnap = resolveDocumentIdentityFromPageSnapshot(entry)
  if (fromSnap) return { _id: fromSnap._id, _typeId: fromSnap._typeId }
  if (!entry || typeof entry !== 'object') return null
  const nested = (entry as { data?: unknown }).data
  if (!nested) return null
  const nestedBound = resolveBoundDocumentIdentityFromPage(nested)
  if (nestedBound) return { _id: nestedBound._id, _typeId: nestedBound._typeId }
  const nestedSnap = resolveDocumentIdentityFromPageSnapshot(nested)
  if (nestedSnap) return { _id: nestedSnap._id, _typeId: nestedSnap._typeId }
  return null
}

function identitiesMatch(
  left: { _id: number; _typeId: string } | null,
  right: FormEventParamIdentity | null | undefined,
): boolean {
  if (!left || !right) return false
  return left._id === right._id && left._typeId === right._typeId
}

/**
 * Найти slotId форм(ы), слушающих данный core/session.
 * Если payload.sessionId задан — только слоты с этим sessionId.
 * Если есть paramIdentity и открыт dialog с тем же документом — предпочесть dialog slot
 * (soft-event на parent core не должен садиться только на вкладку за модалкой).
 */
export function resolveFormSlotsForCoreEvent(input: {
  coreId: string
  payloadSessionId?: string | null
  pagesData: Record<string, unknown>
  dialogs?: Record<string, unknown>
  overlays?: Record<string, unknown>
  fallbackSlotId?: string | null
  activeSessionId?: string | null
  paramIdentity?: FormEventParamIdentity | null
}): string[] {
  const coreId = String(input.coreId ?? '').trim()
  if (!coreId) return []

  const filterSession =
    input.payloadSessionId != null && String(input.payloadSessionId).trim() !== ''
      ? String(input.payloadSessionId).trim()
      : null

  const sessionSlots: string[] = []
  const identityDialogSlots: string[] = []
  const seenSession = new Set<string>()
  const seenIdentity = new Set<string>()

  const considerSession = (slotId: string, entry: unknown) => {
    const sid = readEntrySessionId(entry)
    if (!sid) return
    if (sid !== coreId) return
    if (filterSession && sid !== filterSession) return
    if (seenSession.has(slotId)) return
    seenSession.add(slotId)
    sessionSlots.push(slotId)
  }

  const considerIdentityDialog = (slotId: string, entry: unknown) => {
    if (!input.paramIdentity) return
    if (!identitiesMatch(readEntryDocumentIdentity(entry), input.paramIdentity)) return
    if (seenIdentity.has(slotId)) return
    seenIdentity.add(slotId)
    identityDialogSlots.push(slotId)
  }

  Object.entries(input.pagesData || {}).forEach(([slotId, entry]) => {
    considerSession(String(slotId), entry)
  })
  Object.entries(input.dialogs || {}).forEach(([slotId, entry]) => {
    const id = String(slotId)
    considerSession(id, entry)
    considerIdentityDialog(id, entry)
  })
  Object.entries(input.overlays || {}).forEach(([slotId, entry]) => {
    considerSession(String(slotId), entry)
  })

  if (identityDialogSlots.length > 0) {
    const sessionDialogs = sessionSlots.filter(
      (id) => Boolean(input.dialogs?.[id]) || Boolean(input.overlays?.[id]),
    )
    return [...new Set([...identityDialogSlots, ...sessionDialogs])]
  }

  if (sessionSlots.length > 0) {
    return [...new Set(sessionSlots)]
  }

  // SAME_WINDOW / race: событие на core активной вкладки, слот ещё без sessionId в map.
  const activeSession = String(input.activeSessionId ?? '').trim()
  const fallbackSlot = String(input.fallbackSlotId ?? '').trim()
  if (
    fallbackSlot &&
    activeSession &&
    activeSession === coreId &&
    (!filterSession || filterSession === coreId)
  ) {
    return [fallbackSlot]
  }

  return []
}
